import { useScreenView } from '../../../../../utils/ScreenViewContext';
import { useUser } from '../../../../../utils/UserContext';
import useApi from '../../../../../utils/AxiosWrapper';
import { useQuery } from '@tanstack/react-query';
import { PartnerAgentOrInvite } from '../../../../../types/apiTypes';
import {
  UsersThree,
  MagnifyingGlass,
  X,
  Envelope,
  DotsThreeVertical,
  Trash,
  XCircle,
} from '@phosphor-icons/react';
import { ClipLoader } from 'react-spinners';
import { useState, useRef, useEffect } from 'react';
import { AccentButtonOutline } from '../../../../../shared/Buttons';
import { toast } from 'react-hot-toast';
import { useQueryClient } from '@tanstack/react-query';
import { createPortal } from 'react-dom';
import { HttpStatusCode } from 'axios';
import InvitePartnerAgentModal from './widgets/InvitePartnerAgentModal';
import { Badge } from 'antd';

interface PartnerAgentsHomeProps {
  userType: string;
  currentPage: string;
  currentTab: string;
  setCurrentTab: (tab: string) => void;
  subTab: string;
  setSubTab: (tab: string) => void;
}

function PartnerAgentsHome({
  userType,
  currentPage,
  currentTab,
  setCurrentTab,
  subTab,
  setSubTab,
}: PartnerAgentsHomeProps) {
  const { screenView } = useScreenView();
  const { userId } = useUser();
  const [searchKeyword, setSearchKeyword] = useState('');
  const [isInviteModalOpen, setIsInviteModalOpen] = useState(false);
  const { callProtectedApi } = useApi();
  const queryClient = useQueryClient();
  const {
    data: partnerAgentsAndInvites = [],
    isLoading: loadingPartnerAgentsAndInvites,
  } = useQuery({
    queryKey: ['partnerAgentsAndInvites', userId ?? '', searchKeyword],
    queryFn: async () => {
      const response = await callProtectedApi(
        `/partnerAgentInvites/${userId ?? ''}`,
        'post',
        {
          searchKeyword:
            searchKeyword && searchKeyword?.length > 0
              ? searchKeyword
              : undefined,
        }
      );
      return response?.data?.data?.partnerAgentsAndInvites || [];
    },
    enabled: !!userId,
    staleTime: 15 * 60 * 1000, // 15 minutes
    gcTime: 15 * 60 * 1000, // 15 minutes
  });

  const [popupVisibleMap, setPopupVisibleMap] = useState<Map<string, boolean>>(
    new Map()
  );
  const [loadingDeleteMap, setLoadingDeleteMap] = useState<
    Map<string, boolean>
  >(new Map());
  const [loadingResendMap, setLoadingResendMap] = useState<
    Map<string, boolean>
  >(new Map());
  const [loadingCancelMap, setLoadingCancelMap] = useState<
    Map<string, boolean>
  >(new Map());
  const popupRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  const buttonRefs = useRef<{ [key: string]: HTMLButtonElement | null }>({});

  const togglePopup = (partnerAgentId: string | undefined) => {
    if (!partnerAgentId) return;
    setPopupVisibleMap((prevMap) => {
      const newMap = new Map(prevMap);
      newMap.set(partnerAgentId, !newMap.get(partnerAgentId)); // Toggle visibility
      return newMap;
    });
  };

  const handleSetLoadingDelete = (loading: boolean, partnerAgentId: string) => {
    setLoadingDeleteMap((prevMap) => {
      const newMap = new Map(prevMap);
      newMap.set(partnerAgentId, loading);
      return newMap;
    });
  };

  const handleSetLoadingResend = (loading: boolean, partnerAgentId: string) => {
    setLoadingResendMap((prevMap) => {
      const newMap = new Map(prevMap);
      newMap.set(partnerAgentId, loading);
      return newMap;
    });
  };

  const handleSetLoadingCancel = (loading: boolean, inviteId: string) => {
    setLoadingCancelMap((prevMap) => {
      const newMap = new Map(prevMap);
      newMap.set(inviteId, loading);
      return newMap;
    });
  };

  const removePartnerAgent = async (
    customerId: string,
    partnerAgentId: string
  ) => {
    handleSetLoadingDelete(true, partnerAgentId);
    try {
      const response = await callProtectedApi(
        `/partnerAgentInvites/delete/${customerId}/${partnerAgentId}`,
        'delete',
        {},
        'Error occurred while removing partner agent.'
      );
      if (response?.status === 200) {
        toast.success('Partner agent removed successfully');
        // Refetch the partnerAgents list
        await queryClient.invalidateQueries({
          queryKey: ['partnerAgentsAndInvites'],
        });
      }
    } catch (e: any) {
      console.error('Error occurred while removing partner agent: ', e);
    } finally {
      handleSetLoadingDelete(false, partnerAgentId);
    }
  };

  const cancelPartnerAgentInvite = async (inviteId: string) => {
    handleSetLoadingCancel(true, inviteId);
    try {
      const response = await callProtectedApi(
        `/partnerAgentInvites/cancel`,
        'post',
        { invite_id: inviteId },
        'Error occurred while cancelling partner agent invite.'
      );
      if (response?.status === 200) {
        toast.success('Partner agent invite cancelled successfully');
        // Refetch the partnerAgents list
        await queryClient.invalidateQueries({
          queryKey: ['partnerAgentsAndInvites'],
        });
      }
    } catch (e: any) {
      console.error(
        'Error occurred while cancelling partner agent invite: ',
        e
      );
      toast.error(e?.response?.data?.error || 'Failed to cancel invite');
    } finally {
      handleSetLoadingCancel(false, inviteId);
    }
  };

  const handleClickOutside = (event: MouseEvent) => {
    const newMap = new Map(popupVisibleMap);
    let popupClicked = false;

    // Check if the click was outside any of the visible popups
    partnerAgentsAndInvites.forEach((partnerAgent: PartnerAgentOrInvite) => {
      const popupRef = popupRefs.current[partnerAgent?.object_id ?? ''];
      const buttonRef = buttonRefs.current[partnerAgent?.object_id ?? ''];

      if (
        (popupRef && popupRef.contains(event.target as Node)) ||
        (buttonRef && buttonRef.contains(event.target as Node))
      ) {
        popupClicked = true;
      }
    });

    // If click was outside all popups and buttons, close all popups
    if (!popupClicked) {
      partnerAgentsAndInvites.forEach((partnerAgent: PartnerAgentOrInvite) => {
        newMap.set(partnerAgent?.object_id ?? '', false);
      });
      setPopupVisibleMap(newMap);
    }
  };

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [popupVisibleMap]);

  const renderSearchBar = () => (
    <div
      className={`flex h-[48px] max-h-[48px] w-full min-w-[180px] flex-grow items-center gap-1 rounded-full bg-white px-4 py-3 text-[#7D8191] lg:w-[40%] lg:max-w-[40%]`}
    >
      <MagnifyingGlass size={20} />
      <input
        type="text"
        placeholder="Search"
        className="ml-2 flex-grow bg-transparent text-gray-600 outline-none"
        value={searchKeyword}
        onChange={(e) => {
          setSearchKeyword(e.target.value);
        }}
      />
      {searchKeyword && searchKeyword?.length > 0 && (
        <button
          className="rounded-full p-[2px] hover:bg-gray-200"
          onClick={() => setSearchKeyword('')}
        >
          <X size={20} />
        </button>
      )}
    </div>
  );

  const sendInvite = async ({
    partner_id,
    email,
    first_name,
    last_name,
    updated_at,
  }: {
    partner_id: string;
    email: string;
    first_name: string;
    last_name: string;
    updated_at: Date;
  }) => {
    try {
      const response = await callProtectedApi(
        `/partnerAgentInvites`,
        'post',
        {
          partner_id: partner_id,
          email: email,
          first_name: first_name,
          last_name: last_name,
        },
        'Error occurred while sending agent invite.'
      );
      if (response?.status === HttpStatusCode.Created) {
        toast.success('Successfully resent agent invite.');
      }
    } catch (e: any) {
      console.error('Error occurred while resending partnerAgent invite: ', e);
    }
  };

  const renderScreenView = (screenView: string) => {
    switch (screenView) {
      case 'desktop':
      case 'tablet':
      case 'mobile':
        return (
          <div className="flex h-full w-full flex-col items-center overflow-x-hidden overflow-y-hidden">
            <div className="flex h-full flex-col overflow-x-hidden overflow-y-hidden sm:w-[95%] md:w-full md:px-4 lg:w-[1148px] lg:max-w-[1148px] lg:px-0">
              <div className="flex h-full flex-col gap-y-4 overflow-y-auto pt-4">
                <div className="flex w-full flex-col justify-between sm:h-[112px] sm:max-h-[112px] sm:gap-y-4 lg:h-[48px] lg:flex-row lg:gap-y-0">
                  {renderSearchBar()}
                  <AccentButtonOutline
                    addClass="h-[48px] max-h-[48px] rounded-full py-[14px] px-4 space-x-1 w-[196px]"
                    onClick={() => setIsInviteModalOpen(true)}
                  >
                    <Envelope size={20} />
                    <p>Invite Agent</p>
                  </AccentButtonOutline>
                </div>
                {!loadingPartnerAgentsAndInvites ? (
                  <>
                    {partnerAgentsAndInvites &&
                    partnerAgentsAndInvites?.length > 0 ? (
                      <div className="flex h-full w-full flex-col overflow-x-auto rounded-[8px] border-[1px] border-bordergray bg-white">
                        <table className="w-full min-w-full overflow-x-auto bg-white">
                          <thead className="h-[50px] max-h-[50px] bg-primarybg">
                            <tr className="h-[50px] max-h-[50px] overflow-hidden">
                              <th className="h-[50px] max-h-[50px] items-center overflow-hidden px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary">
                                First Name
                              </th>
                              <th className="h-[50px] max-h-[50px] px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary">
                                Last Name
                              </th>
                              <th className="h-[50px] max-h-[50px] px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary sm:w-[40%] lg:w-auto">
                                Email
                              </th>
                              <th className="h-[50px] max-h-[50px] px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary">
                                Status
                              </th>
                              <th className="h-[50px] max-h-[50px] w-[25%] px-4 py-2 text-left text-[14px] font-[600] leading-[18px] text-primary"></th>
                            </tr>
                          </thead>
                          <tbody className="table-fixed overflow-x-auto bg-white">
                            {partnerAgentsAndInvites.map(
                              (
                                partnerAgent: PartnerAgentOrInvite,
                                index: number
                              ) => (
                                <tr
                                  key={index}
                                  className="h-[50px] max-h-[50px] overflow-hidden border-b border-gray-200"
                                >
                                  <td className="h-[50px] max-h-[50px] overflow-hidden px-4 py-2 text-[14px] text-primary">
                                    {partnerAgent?.first_name}
                                  </td>
                                  <td className="h-[50px] max-h-[50px] overflow-hidden px-4 py-2 text-[14px] text-primary">
                                    {partnerAgent?.last_name}
                                  </td>
                                  <td
                                    className={`flex h-[50px] max-h-[50px] w-[30%] items-center justify-start px-4 py-2 text-[14px]`}
                                  >
                                    {partnerAgent?.email}
                                  </td>
                                  <td className="h-[50px] max-h-[50px] overflow-hidden px-4 py-2">
                                    {partnerAgent?.status === 'active' ? (
                                      <Badge
                                        color="green"
                                        text="Active"
                                        className="font-medium"
                                      />
                                    ) : partnerAgent?.status === 'cancelled' ? (
                                      <Badge
                                        color="red"
                                        text="Cancelled"
                                        className="font-medium"
                                      />
                                    ) : (
                                      <Badge
                                        color="gold"
                                        text="Invited"
                                        className="font-medium"
                                      />
                                    )}
                                  </td>
                                  <td className="h-[50px] max-h-[50px] overflow-hidden px-4 py-2 text-[14px] text-primary">
                                    {!loadingDeleteMap.get(
                                      partnerAgent?.object_id
                                    ) &&
                                    !loadingCancelMap.get(
                                      partnerAgent?.object_id
                                    ) ? (
                                      <button
                                        ref={(el) => {
                                          buttonRefs.current[
                                            partnerAgent?.object_id ?? ''
                                          ] = el;
                                        }}
                                        onClick={() =>
                                          togglePopup(
                                            partnerAgent?.object_id ?? ''
                                          )
                                        }
                                        className="relative"
                                      >
                                        <DotsThreeVertical
                                          size={26}
                                          weight="bold"
                                        />
                                        {popupVisibleMap.get(
                                          partnerAgent?.object_id ?? ''
                                        ) &&
                                          createPortal(
                                            <div
                                              ref={(el) => {
                                                popupRefs.current[
                                                  partnerAgent?.object_id ?? ''
                                                ] = el;
                                              }}
                                              className="fixed z-[9999] w-44 rounded-lg border-[1px] border-bordergray bg-white shadow-lg drop-shadow-xl"
                                              style={{
                                                left:
                                                  buttonRefs.current[
                                                    partnerAgent?.object_id ??
                                                      ''
                                                  ]?.getBoundingClientRect()
                                                    .left ?? 0,
                                                top:
                                                  buttonRefs.current[
                                                    partnerAgent?.object_id ??
                                                      ''
                                                  ]?.getBoundingClientRect()
                                                    .top ?? 0,
                                                transform:
                                                  'translateX(-100%) translateX(-8px)',
                                              }}
                                            >
                                              {partnerAgent?.is_partner_agent ? (
                                                <div>
                                                  <button
                                                    onClick={async (e: any) => {
                                                      e.stopPropagation();
                                                      await removePartnerAgent(
                                                        userId ?? '',
                                                        partnerAgent?.object_id ??
                                                          ''
                                                      );
                                                      togglePopup(
                                                        partnerAgent?.object_id ??
                                                          ''
                                                      );
                                                    }}
                                                    className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-red-500 hover:bg-gray-100"
                                                  >
                                                    <Trash size={20} />
                                                    <p>Remove Agent</p>
                                                  </button>
                                                </div>
                                              ) : partnerAgent?.status ===
                                                'pending' ? (
                                                <div>
                                                  <button
                                                    onClick={async (e: any) => {
                                                      e.stopPropagation();
                                                      handleSetLoadingResend(
                                                        true,
                                                        partnerAgent?.object_id ??
                                                          ''
                                                      );
                                                      await sendInvite({
                                                        partner_id:
                                                          userId ?? '',
                                                        email:
                                                          partnerAgent?.email ??
                                                          '',
                                                        first_name:
                                                          partnerAgent?.first_name ??
                                                          '',
                                                        last_name:
                                                          partnerAgent?.last_name ??
                                                          '',
                                                        updated_at:
                                                          partnerAgent?.updated_at ??
                                                          new Date(),
                                                      });
                                                      handleSetLoadingResend(
                                                        false,
                                                        partnerAgent?.object_id ??
                                                          ''
                                                      );
                                                      togglePopup(
                                                        partnerAgent?.object_id ??
                                                          ''
                                                      );
                                                    }}
                                                    className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-primary hover:bg-gray-100"
                                                    disabled={loadingResendMap.get(
                                                      partnerAgent?.object_id
                                                    )}
                                                  >
                                                    {loadingResendMap.get(
                                                      partnerAgent?.object_id
                                                    ) ? (
                                                      <ClipLoader
                                                        color="#1D213B"
                                                        loading={true}
                                                        size={16}
                                                        aria-label="Loading Spinner"
                                                        data-testid="loader"
                                                      />
                                                    ) : (
                                                      <Envelope size={20} />
                                                    )}
                                                    <p>
                                                      {loadingResendMap.get(
                                                        partnerAgent?.object_id
                                                      )
                                                        ? 'Sending...'
                                                        : 'Resend Invite'}
                                                    </p>
                                                  </button>
                                                  <button
                                                    onClick={async (e: any) => {
                                                      e.stopPropagation();
                                                      await cancelPartnerAgentInvite(
                                                        partnerAgent?.object_id ??
                                                          ''
                                                      );
                                                      togglePopup(
                                                        partnerAgent?.object_id ??
                                                          ''
                                                      );
                                                    }}
                                                    className="flex w-full flex-row items-center space-x-1 px-4 py-2 text-left align-middle text-red-500 hover:bg-gray-100"
                                                    disabled={loadingCancelMap.get(
                                                      partnerAgent?.object_id
                                                    )}
                                                  >
                                                    {loadingCancelMap.get(
                                                      partnerAgent?.object_id
                                                    ) ? (
                                                      <ClipLoader
                                                        color="#ef4444"
                                                        loading={true}
                                                        size={16}
                                                        aria-label="Loading Spinner"
                                                        data-testid="loader"
                                                      />
                                                    ) : (
                                                      <XCircle size={20} />
                                                    )}
                                                    <p>
                                                      {loadingCancelMap.get(
                                                        partnerAgent?.object_id
                                                      )
                                                        ? 'Cancelling...'
                                                        : 'Cancel Invite'}
                                                    </p>
                                                  </button>
                                                </div>
                                              ) : (
                                                <div className="px-4 py-2 text-gray-400">
                                                  No actions available
                                                </div>
                                              )}
                                            </div>,
                                            document.body
                                          )}
                                      </button>
                                    ) : (
                                      <ClipLoader
                                        color="#1D213B"
                                        loading={
                                          loadingDeleteMap.get(
                                            partnerAgent?.object_id
                                          ) ||
                                          loadingCancelMap.get(
                                            partnerAgent?.object_id
                                          )
                                        }
                                        size={20}
                                        aria-label="Loading Spinner"
                                        data-testid="loader"
                                        className="h-full w-full justify-center"
                                      />
                                    )}
                                  </td>
                                </tr>
                              )
                            )}
                          </tbody>
                        </table>
                      </div>
                    ) : (
                      <div className="flex h-full min-h-[300px] w-full flex-col items-center justify-center space-y-2">
                        <div className="flex h-[64px] w-[64px] items-center justify-center rounded-[4px] border-[1px] border-bordergray bg-[#F5F6F7]">
                          <UsersThree
                            size={40}
                            className="text-graysecondary"
                          />
                        </div>
                        <p className="text-[14px] leading-[20px] text-graysecondary">
                          No Agents or Agent Invites Found
                        </p>
                      </div>
                    )}
                  </>
                ) : (
                  <div className="flex h-full w-full items-center justify-center">
                    <ClipLoader
                      color="#1D213B"
                      loading={loadingPartnerAgentsAndInvites}
                      size={60}
                      aria-label="Loading Spinner"
                      data-testid="loader"
                      className="flex h-full w-full items-center justify-center"
                    />
                  </div>
                )}
              </div>
            </div>

            <InvitePartnerAgentModal
              isOpen={isInviteModalOpen}
              onClose={() => setIsInviteModalOpen(false)}
            />
          </div>
        );
    }
  };

  return <>{renderScreenView(screenView)}</>;
}

export default PartnerAgentsHome;
